import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TreePanel from './TreePanel';
import DetailPanel from './DetailPanel';
import { cn } from '../../lib/utils';
import { useModelContext } from '../Layout/ModelContext';

interface CalculationResultsSplitViewProps {
    currentState: any;
    reloadState: () => void;
    userRights: { right_to_write: boolean };
    scenarioId?: number | null;
}

export default function CalculationResultsSplitView({
    currentState,
    reloadState,
    userRights,
    scenarioId = null,
}: CalculationResultsSplitViewProps) {
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [searchString, setSearchString] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const tree = currentState['Output']?.tree || { items: {}, rootId: '42' };
    const inputTree = currentState['Input']?.tree || { items: {}, rootId: '42' };
    const navigate = useNavigate();
    const { currentEntity } = useModelContext();

    // Reset selection when entity changes (sensitivity/scenario switch)
    useEffect(() => {
        setSelectedItemId(null);
    }, [currentEntity?.id]);

    // Navigate to a model_data by ID, using kind to avoid input/output ID collisions
    const handleNavigate = useCallback((modelDataId: string, kind?: string) => {
        // If explicitly an input, navigate to the Financial Model Editor
        if (kind === 'input') {
            navigate('/app/models/editor/financial', {
                state: {
                    entity: currentEntity,
                    targetInputModelDataId: modelDataId,
                },
            });
            return;
        }
        // Output: select it in the current tree
        if (tree.items[modelDataId]) {
            setSelectedItemId(modelDataId);
            return;
        }
    }, [tree.items, navigate, currentEntity]);

    const selectedItem = useMemo(() => {
        if (!selectedItemId) return null;
        return tree.items[selectedItemId] || null;
    }, [selectedItemId, tree.items, currentState]);

    return (
        <div className="flex h-full min-h-0">
            {/* Left: Tree Panel */}
            <div className={cn(
                "border-r border-slate-200 flex flex-col overflow-hidden bg-white shrink-0 transition-all duration-300",
                sidebarCollapsed ? "w-12" : "w-1/4 min-w-[200px] max-w-[300px]"
            )}>
                {/* Sidebar header with toggle */}
                <div className={cn(
                    "p-3 border-b border-slate-100 flex items-center",
                    sidebarCollapsed ? "justify-center" : "justify-between"
                )}>
                    {!sidebarCollapsed && (
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Items
                        </span>
                    )}
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className="text-slate-400 hover:text-primary transition-colors"
                        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <span className="material-symbols-outlined text-sm">
                            {sidebarCollapsed ? 'chevron_right' : 'side_navigation'}
                        </span>
                    </button>
                </div>

                {/* Tree content — hidden when collapsed */}
                {!sidebarCollapsed && (
                    <TreePanel
                        tree={tree}
                        selectedItemId={selectedItemId}
                        onSelectItem={setSelectedItemId}
                        searchString={searchString}
                        onSearchChange={setSearchString}
                        currentEntity={currentEntity}
                    />
                )}
            </div>

            {/* Right: Detail Panel */}
            <div className="flex-1 overflow-hidden bg-white">
                <DetailPanel
                    selectedItem={selectedItem}
                    tree={tree}
                    currentState={currentState}
                    reloadState={reloadState}
                    userRights={userRights}
                    onSelectItem={handleNavigate}
                    scenarioId={scenarioId}
                />
            </div>
        </div>
    );
}
